import type { ProductType } from '@/types/productType'
import type { EntityRelation, Signals } from '@/types/types'

export enum RolePermission {
  ALLOCATION_READ = 'allocation:read',
  ALLOCATION_WRITE = 'allocation:write',
  ALLOCATION_REQUEST_READ = 'allocation_request:read',
  ALLOCATION_REQUEST_WRITE = 'allocation_request:write',
  CLIENT_READ = 'client:read',
  CLIENT_WRITE = 'client:write',
  CONTRACT_READ = 'contract:read',
  CONTRACT_WRITE = 'contract:write',
  COMPLAINT_READ = 'complaint:read',
  COMPLAINT_WRITE = 'complaint:write',
  CLAIM_READ = 'claim:read',
  CLAIM_WRITE = 'claim:write',
  I_BERICHT_READ = 'i_bericht:read',
  I_BERICHT_WRITE = 'i_bericht:write',
  MUNICIPALITY_READ = 'municipality:read',
  MUNICIPALITY_WRITE = 'municipality:write',
  SUPPLIER_READ = 'supplier:read',
  SUPPLIER_WRITE = 'supplier:write',
  PAYMENT_READ = 'payment:read',
  PAYMENT_WRITE = 'payment:write',
  PRODUCT_READ = 'product:read',
  PRODUCT_WRITE = 'product:write',
  PRODUCT_GROUP_READ = 'product_group:read',
  PRODUCT_GROUP_WRITE = 'product_group:write',
  QUOTATION_READ = 'quotation:read',
  QUOTATION_WRITE = 'quotation:write',
  ROLE_MANAGEMENT_READ = 'role_management:read',
  ROLE_MANAGEMENT_WRITE = 'role_management:write',
  USER_READ = 'user:read',
  USER_WRITE = 'user:write',
  REPORT_READ = 'report:read',
  REPORT_WRITE = 'report:write',
}

// NOTE: When editing this enum make sure to also edit the `TaskTypeOf` enum in `taskType.ts`
export enum TaskPermission {
  // Municipality
  ALLOCATION_REQUEST_PARKED_NOTIFICATION = 'allocation_request_parked:notification',
  ALLOCATION_REQUEST_PARKED_READ = 'allocation_request_parked:read',
  ALLOCATION_REQUEST_RECEIVED_MUNICIPALITY_NOTIFICATION = 'allocation_request_received_municipality:notification',
  ALLOCATION_REQUEST_RECEIVED_MUNICIPALITY_READ = 'allocation_request_received_municipality:read',
  ASSESS_FULFILLMENT_BUDGET_NOTIFICATION = 'assess_fulfillment_budget:notification',
  ASSESS_FULFILLMENT_BUDGET_READ = 'assess_fulfillment_budget:read',
  ASSESS_FULFILLMENT_DEADLINE_NOTIFICATION = 'assess_fulfillment_deadline:notification',
  ASSESS_FULFILLMENT_DEADLINE_READ = 'assess_fulfillment_deadline:read',
  ASSESS_FULFILLMENT_EXCEEDED_NOTIFICATION = 'assess_fulfillment_exceeded:notification',
  ASSESS_FULFILLMENT_EXCEEDED_READ = 'assess_fulfillment_exceeded:read',
  ASSESS_FULFILLMENT_MONTHLY_BUDGET_NOTIFICATION = 'assess_fulfillment_monthly_budget:notification',
  ASSESS_FULFILLMENT_MONTHLY_BUDGET_READ = 'assess_fulfillment_monthly_budget:read',
  CAK_MESSAGE_RECEIVED_CLIENT_DECEASED_NOTIFICATION = 'cak_message_received_client_deceased:notification',
  CAK_MESSAGE_RECEIVED_CLIENT_DECEASED_READ = 'cak_message_received_client_deceased:read',
  CAK_MESSAGE_RECEIVED_CLIENT_MOVED_NOTIFICATION = 'cak_message_received_client_moved:notification',
  CAK_MESSAGE_RECEIVED_CLIENT_MOVED_READ = 'cak_message_received_client_moved:read',
  CHAT_MESSAGE_BY_MUNICIPALITY_NOTIFICATION = 'chat_message_by_municipality:notification',
  CHAT_MESSAGE_BY_MUNICIPALITY_READ = 'chat_message_by_municipality:read',
  COMPLAINT_RECEIVED_BY_COMPLAINTS_CENTER_NOTIFICATION = 'complaint_received_by_complaints_center:notification',
  COMPLAINT_RECEIVED_BY_COMPLAINTS_CENTER_READ = 'complaint_received_by_complaints_center:read',
  COMPLAINT_RESOLVED_BY_SUPPLIER_NOTIFICATION = 'complaint_resolved_by_supplier:notification',
  COMPLAINT_RESOLVED_BY_SUPPLIER_READ = 'complaint_resolved_by_supplier:read',
  END_DATE_RECEIVED_NOTIFICATION = 'end_date_received:notification',
  END_DATE_RECEIVED_READ = 'end_date_received:read',
  FULFILLMENT_CREDIT_EXPECTED_NOTIFICATION = 'fulfillment_credit_expected:notification',
  FULFILLMENT_CREDIT_EXPECTED_READ = 'fulfillment_credit_expected:read',
  MISSING_CREDITOR_NUMBER_NOTIFICATION = 'missing_creditor_number:notification',
  MISSING_CREDITOR_NUMBER_READ = 'missing_creditor_number:read',
  NEW_USER_NOTIFICATION = 'new_user:notification',
  NEW_USER_READ = 'new_user:read',
  QUOTATION_REQUEST_RESPONDED_NOTIFICATION = 'quotation_request_responded:notification',
  QUOTATION_REQUEST_RESPONDED_READ = 'quotation_request_responded:read',
  START_DATE_RECEIVED_NOTIFICATION = 'start_date_received:notification',
  START_DATE_RECEIVED_READ = 'start_date_received:read',

  // Supplier
  ALLOCATION_CHANGED_NOTIFICATION = 'allocation_changed:notification',
  ALLOCATION_CHANGED_READ = 'allocation_changed:read',
  ALLOCATION_RECEIVED_NOTIFICATION = 'allocation_received:notification',
  ALLOCATION_RECEIVED_READ = 'allocation_received:read',
  ALLOCATION_REQUEST_RECEIVED_SUPPLIER_NOTIFICATION = 'allocation_request_received_supplier:notification',
  ALLOCATION_REQUEST_RECEIVED_SUPPLIER_READ = 'allocation_request_received_supplier:read',
  CHAT_MESSAGE_BY_SUPPLIER_NOTIFICATION = 'chat_message_by_supplier:notification',
  CHAT_MESSAGE_BY_SUPPLIER_READ = 'chat_message_by_supplier:read',
  COMPLAINT_FORWARDED_TO_SUPPLIER_NOTIFICATION = 'complaint_forwarded_to_supplier:notification',
  COMPLAINT_FORWARDED_TO_SUPPLIER_READ = 'complaint_forwarded_to_supplier:read',
  COMPLAINT_RESOLVED_NOT_SATISFIED_NOTIFICATION = 'complaint_resolved_not_satisfied:notification',
  COMPLAINT_RESOLVED_NOT_SATISFIED_READ = 'complaint_resolved_not_satisfied:read',
  FULFILLMENT_APPROVED_NOTIFICATION = 'fulfillment_approved:notification',
  FULFILLMENT_APPROVED_READ = 'fulfillment_approved:read',
  FULFILLMENT_IN_PROGRESS_NOTIFICATION = 'fulfillment_in_progress:notification',
  FULFILLMENT_IN_PROGRESS_READ = 'fulfillment_in_progress:read',
  FULFILLMENT_REJECTED_NOTIFICATION = 'fulfillment_rejected:notification',
  FULFILLMENT_REJECTED_READ = 'fulfillment_rejected:read',
  QUOTATION_ACCEPTED_NOTIFICATION = 'quotation_accepted:notification',
  QUOTATION_ACCEPTED_READ = 'quotation_accepted:read',
  QUOTATION_DECLINED_NOTIFICATION = 'quotation_declined:notification',
  QUOTATION_DECLINED_READ = 'quotation_declined:read',
  QUOTATION_REQUEST_RECEIVED_NOTIFICATION = 'quotation_request_received:notification',
  QUOTATION_REQUEST_RECEIVED_READ = 'quotation_request_received:read',
  SUPPLIER_FULFILLMENT_CREDIT_EXPECTED_NOTIFICATION = 'supplier_fulfillment_credit_expected:notification',
  SUPPLIER_FULFILLMENT_CREDIT_EXPECTED_READ = 'supplier_fulfillment_credit_expected:read',
}

export type RoleType = {
  id: string
  name: string
  permissions: Array<RolePermission | TaskPermission>
  products?: Array<Pick<ProductType, 'id' | 'name'>>
  relations?: EntityRelation[]
  signals?: Signals
}

export type RoleTypeFlat = Omit<RoleType, 'products'> & {
  products: Array<ProductType['id']>
}
